import { Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter, Button, VStack, Text, FormControl, FormLabel, HStack, Box, Spinner } from '@chakra-ui/react'
import { ICourseDeadlines } from '@/shared/ICourseDeadlines';
import React from 'react';
import { ICourse } from '@/shared/ICourse';
import { CourseStorageService } from '@/app/services/CourseStorageService';
import { useOperation } from '@/app/components/useOperation';
import { ErrorMessage } from '@/app/components/ErrorMessage';
import { DeadlineDatePicker } from '@/app/components/DeadlineDatePicker';
import { WorkflowService } from '@/shared/WorkflowService';
import { formatDate } from '@/shared/parse';

export const EditDeadlineDialog = (props: {
  course: ICourse;
  onCourseDeadlinesChanged: (course: ICourse) => void;
  onClose: () => void;
}) => {

  const [deadlines, setDeadlines] = React.useState<ICourseDeadlines>(props.course.deadlines || {});

  // Calculate current deadline based on current deadlines state and course status
  const currentDeadline = React.useMemo(() => {
    const hasBeenReviewed = props.course.hasBeenReviewed ?? false;
    // Create a temporary course object with updated deadlines for calculation
    const courseWithUpdatedDeadlines = { ...props.course, deadlines };
    return WorkflowService.getCurrentDeadline(courseWithUpdatedDeadlines, hasBeenReviewed);
  }, [deadlines, props.course]);

  const saveDeadlines = async () => {
    const updatedCourse = await CourseStorageService.patchCourse(props.course.id, {
      deadlines
    });
    props.onCourseDeadlinesChanged(updatedCourse);
    props.onClose();
  }

  const [onSave, saveErrorMessage, isProcessing] = useOperation(saveDeadlines, [props.course, deadlines]);

  return (
    <Modal
      size={'xl'}
      isOpen
      onClose={props.onClose}
      preserveScrollBarGap
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Course Deadlines</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <VStack spacing={6} align="stretch">
            <Text fontSize="md" color="fg.muted">
              Set deadlines for course submission phases
            </Text>            {/* Show earliest deadline */}
            <ErrorMessage error={saveErrorMessage} />
            <HStack spacing={8} align="start">
              {/* Step 1 Deadlines */}
              <Box flex="1">
                <VStack spacing={4} align="stretch">
                  <Text fontSize="md" fontWeight="semibold">Step 1</Text>

                  <FormControl>
                    <FormLabel>Draft Submission</FormLabel>
                    <DeadlineDatePicker
                      label="Draft Submission"
                      date={deadlines.step1DraftSubmissionDate}
                      onDateChange={(date) => setDeadlines({ ...deadlines, step1DraftSubmissionDate: date })}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Internal Review</FormLabel>
                    <DeadlineDatePicker
                      label="Internal Review"
                      date={deadlines.step1InternalReviewDate}
                      onDateChange={(date) => setDeadlines({ ...deadlines, step1InternalReviewDate: date })}
                      minDate={deadlines.step1DraftSubmissionDate}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Final Submission</FormLabel>
                    <DeadlineDatePicker
                      label="Final Submission"
                      date={deadlines.step1FinalSubmissionDate}
                      onDateChange={(date) => setDeadlines({ ...deadlines, step1FinalSubmissionDate: date })}
                      minDate={deadlines.step1InternalReviewDate}
                    />
                  </FormControl>
                </VStack>
              </Box>

              {/* Step 2 Deadlines */}
              <Box flex="1">
                <VStack spacing={4} align="stretch">
                  <Text fontSize="md" fontWeight="semibold">Step 2</Text>
                  <FormControl>
                    <FormLabel>Draft Submission</FormLabel>
                    <DeadlineDatePicker
                      label="Draft Submission"
                      date={deadlines.step2DraftSubmissionDate}
                      onDateChange={(date) => setDeadlines({ ...deadlines, step2DraftSubmissionDate: date })}
                      minDate={deadlines.step1FinalSubmissionDate}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Internal Review</FormLabel>
                    <DeadlineDatePicker
                      label="Internal Review"
                      date={deadlines.step2InternalReviewDate}
                      onDateChange={(date) => setDeadlines({ ...deadlines, step2InternalReviewDate: date })}
                      minDate={deadlines.step2DraftSubmissionDate}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Final Submission</FormLabel>
                    <DeadlineDatePicker
                      label="Final Submission"
                      date={deadlines.step2FinalSubmissionDate}
                      onDateChange={(date) => setDeadlines({ ...deadlines, step2FinalSubmissionDate: date })}
                      minDate={deadlines.step2InternalReviewDate}
                    />
                  </FormControl>

                </VStack>
              </Box>
            </HStack>

            {/* Calculated Current Deadline (Read-only) */}
            <Box mt={4} p={4} bg="gray.50" borderRadius="md">
              <VStack align="stretch" spacing={2}>
                <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                  Current Deadline (Calculated)
                </Text>
                <Text fontSize="sm" color="gray.600">
                  {currentDeadline ? formatDate(currentDeadline) : "No active deadline"}
                </Text>
              </VStack>
            </Box>

          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant='solid' mr={3} onClick={onSave} isDisabled={isProcessing}><HStack spacing={2}>{isProcessing && <Spinner size="sm" />}<Text>{isProcessing ? 'Saving...' : 'Save'}</Text></HStack></Button>
          <Button variant='outline' onClick={props.onClose}>Cancel</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
